import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, EmptyState, LinkButton, TBody, THead, Table } from '@/components/ui';
import { useMarket } from '@/providers/market';
import {
  FilterBar,
  Hero,
  LiveFeed,
  SpotlightRail,
  TickerBar,
  TokenCard,
  TokenCardSkeleton,
  TokenRow,
  TokenRowHeader,
} from '@/components/explore';
import type { PhaseFilter, SortKey, SpotlightPreset, ViewMode } from '@/components/explore';
import type { DemoToken } from '@/lib/demo/types';
import { compact } from '@/lib/format';

const PAGE_SIZE = 12;
const SKELETON_COUNT = 9;

const SORT_STORAGE_KEY = 'bull.explore.sort';
const VIEW_STORAGE_KEY = 'bull.explore.view';

const SORT_KEYS: readonly SortKey[] = [
  'trending',
  'newest',
  'volume',
  'mcap',
  'floor',
  'progress',
  'gainers',
  'losers',
];
const VIEW_KEYS: readonly ViewMode[] = ['grid', 'list'];

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored && (allowed as readonly string[]).includes(stored)) return stored as T;
  } catch {
    /* storage unavailable (private mode, blocked cookies) — fall through */
  }
  return fallback;
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* non-fatal */
  }
}

const num = (n: number) => (Number.isFinite(n) ? n : 0);

/** Composite: money moving, weighted by momentum and trade count. */
function trendScore(token: DemoToken): number {
  return (
    num(token.volume24hEth) * (1 + Math.max(-0.9, num(token.change24h))) +
    num(token.trades24h) * 0.01 +
    num(token.floorRatio)
  );
}

function sortTokens(list: DemoToken[], sort: SortKey): DemoToken[] {
  const sorted = [...list];
  switch (sort) {
    case 'newest':
      return sorted.sort((a, b) => b.createdAt - a.createdAt);
    case 'volume':
      return sorted.sort((a, b) => num(b.volume24hEth) - num(a.volume24hEth));
    case 'mcap':
      return sorted.sort((a, b) => num(b.marketCapEth) - num(a.marketCapEth));
    case 'floor':
      return sorted.sort((a, b) => num(b.floorRatio) - num(a.floorRatio));
    case 'progress':
      return sorted.sort((a, b) => num(b.graduationProgress) - num(a.graduationProgress));
    case 'gainers':
      return sorted.sort((a, b) => num(b.change24h) - num(a.change24h));
    case 'losers':
      return sorted.sort((a, b) => num(a.change24h) - num(b.change24h));
    case 'trending':
    default:
      return sorted.sort((a, b) => trendScore(b) - trendScore(a));
  }
}

export default function Explore() {
  const { tokens, ready } = useMarket();

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>(() =>
    readStored(SORT_STORAGE_KEY, SORT_KEYS, 'trending'),
  );
  const [phase, setPhase] = useState<PhaseFilter>('all');
  const [tag, setTag] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(() => readStored(VIEW_STORAGE_KEY, VIEW_KEYS, 'grid'));
  const [limit, setLimit] = useState(PAGE_SIZE);

  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Explore — bull.markets';
  }, []);

  useEffect(() => writeStored(SORT_STORAGE_KEY, sort), [sort]);
  useEffect(() => writeStored(VIEW_STORAGE_KEY, view), [view]);
  useEffect(() => setLimit(PAGE_SIZE), [query, sort, phase, tag]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched = tokens.filter((token) => {
      if (phase !== 'all' && token.phase !== phase) return false;
      if (tag && !token.tags.includes(tag)) return false;
      if (!needle) return true;
      return (
        token.name.toLowerCase().includes(needle) ||
        token.symbol.toLowerCase().includes(needle) ||
        token.address.toLowerCase().includes(needle)
      );
    });
    return sortTokens(matched, sort);
  }, [tokens, query, phase, tag, sort]);

  const visible = useMemo(() => filtered.slice(0, limit), [filtered, limit]);

  const counts = useMemo(
    () => ({
      tokens: filtered.length,
      trading: filtered.filter((token) => token.phase === 'trading').length,
    }),
    [filtered],
  );

  const clearFilters = useCallback(() => {
    setQuery('');
    setPhase('all');
    setTag(null);
  }, []);

  const applyPreset = useCallback((preset: SpotlightPreset) => {
    setQuery('');
    setTag(null);
    if (preset === 'graduating') {
      setPhase('graduating');
      setSort('progress');
    } else if (preset === 'floors') {
      setPhase('all');
      setSort('floor');
    } else {
      setPhase('all');
      setSort('newest');
    }
    resultsRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, []);

  const hasFilters = query.trim().length > 0 || phase !== 'all' || tag !== null;

  return (
    <div className="pb-20">
      <TickerBar />
      <Hero />

      {ready && tokens.length > 0 ? (
        <div className="shell py-6">
          <SpotlightRail onSeeAll={applyPreset} />
        </div>
      ) : null}

      <FilterBar
        query={query}
        onQuery={setQuery}
        sort={sort}
        onSort={setSort}
        phase={phase}
        onPhase={setPhase}
        tag={tag}
        onTag={setTag}
        view={view}
        onView={setView}
        count={counts}
      />

      <div className="shell pt-6">
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <div ref={resultsRef} className="min-w-0 scroll-mt-36">
            {!ready ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:gap-4 xl:grid-cols-3">
                {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                  <TokenCardSkeleton key={i} />
                ))}
              </div>
            ) : tokens.length === 0 ? (
              <EmptyState
                title="No tokens launched yet"
                description="The launchpad is live and the floor mechanic is armed. Be the first curve on it."
                action={
                  <LinkButton to="/launch" variant="primary">
                    Launch the first token
                  </LinkButton>
                }
              />
            ) : filtered.length === 0 ? (
              <EmptyState
                title="Nothing matches those filters"
                description="Widen the search, or reset to the full market."
                action={
                  <Button variant="outline" onClick={clearFilters}>
                    Clear filters
                  </Button>
                }
              />
            ) : view === 'grid' ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:gap-4 xl:grid-cols-3">
                {visible.map((token) => (
                  <TokenCard key={token.address} token={token} />
                ))}
              </div>
            ) : (
              <div className="scroll-x rounded-xl border border-line bg-surface">
                <div className="min-w-[62rem]">
                  <Table>
                    <THead>
                      <TokenRowHeader sort={sort} onSort={setSort} />
                    </THead>
                    <TBody>
                      {visible.map((token, i) => (
                        <TokenRow key={token.address} token={token} rank={i + 1} />
                      ))}
                    </TBody>
                  </Table>
                </div>
              </div>
            )}

            {ready && filtered.length > 0 ? (
              <div className="mt-6 flex flex-col items-center gap-2">
                {visible.length < filtered.length ? (
                  <Button variant="outline" onClick={() => setLimit((l) => l + PAGE_SIZE)}>
                    Load more
                  </Button>
                ) : null}
                <p className="font-mono text-2xs text-subtle">
                  Showing {compact(visible.length, 0)} of {compact(filtered.length, 0)}
                  {hasFilters ? ' matching tokens' : ' tokens'}
                </p>
              </div>
            ) : null}
          </div>

          <LiveFeed />
        </div>
      </div>
    </div>
  );
}
